import React, { useState, useEffect } from 'react';
import { useNavigate, Link, Routes, Route, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import {
  User, Music, Upload, FileText, LogOut, Settings,
  CheckCircle, Clock, AlertCircle, ChevronRight,
  Globe, Instagram, Youtube, ExternalLink, Home,
  PenTool, Link as LinkIcon, Edit, Calendar,
  Menu, X, Layers, Megaphone, Scale, Download,
  Image, FolderOpen
} from 'lucide-react';
import LegalVault from './LegalVault';
import LinksManager from './LinksManager';
import ProfileEditor from './ProfileEditor';
import Appearances from './Appearances';
import Submissions from './Submissions';
import MediaVault from './MediaVault';
import './ArtistDashboard.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ArtistDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [artistData, setArtistData] = useState(null);
  const [profile, setProfile] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [agreements, setAgreements] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const getToken = () => localStorage.getItem('artistToken');

  const getHeaders = () => ({
    'Authorization': `Bearer ${getToken()}`,
    'Content-Type': 'application/json'
  });

  useEffect(() => {
    const token = getToken();
    if (!token) {
      navigate('/artist/login');
      return;
    }
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  // Close drawer on route change
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  const fetchDashboardData = async () => {
    try {
      // Fetch artist info and profile
      const meRes = await fetch(`${API_URL}/api/artist/me`, {
        headers: getHeaders()
      });

      if (!meRes.ok) {
        if (meRes.status === 401 || meRes.status === 403) {
          localStorage.removeItem('artistToken');
          localStorage.removeItem('artistInfo');
          navigate('/artist/login');
          return;
        }
        throw new Error('Failed to fetch data');
      }

      const data = await meRes.json();
      setArtistData(data.account);
      setProfile(data.profile);

      // Fetch submissions
      const subsRes = await fetch(`${API_URL}/api/artist/submissions`, {
        headers: getHeaders()
      });
      if (subsRes.ok) {
        setSubmissions(await subsRes.json());
      }

      // Fetch agreements
      const agreementsRes = await fetch(`${API_URL}/api/artist/agreements`, {
        headers: getHeaders()
      });
      if (agreementsRes.ok) {
        setAgreements(await agreementsRes.json());
      }

    } catch (error) {
      console.error('Dashboard error:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('artistToken');
    localStorage.removeItem('artistInfo');
    toast.success('Logged out successfully');
    navigate('/artist/login');
  };

  const getProfileCompletion = () => {
    if (!profile) return 0;
    const fields = [
      'artist_type', 'main_genre', 'bio', 'country', 'profile_image',
      'streaming_ids', 'social_links'
    ];
    const completed = fields.filter(f => {
      const val = profile[f];
      if (typeof val === 'object' && val !== null) {
        return Object.values(val).some(v => v);
      }
      return Boolean(val);
    }).length;
    return Math.round((completed / fields.length) * 100);
  };

  const getUnsignedAgreements = () => {
    return agreements.filter(a => !a.signed);
  };

  if (loading) {
    return (
      <div className="artist-dashboard loading">
        <div className="loading-spinner"></div>
        <p>Loading your dashboard...</p>
      </div>
    );
  }

  const profileCompletion = getProfileCompletion();
  const unsignedAgreements = getUnsignedAgreements();

  return (
    <div className="artist-dashboard">
      {/* Sidebar — visible on tablet (icon rail) and desktop (full) */}
      <aside className="dashboard-sidebar">
        <div className="sidebar-header">
          <Link to="/" className="sidebar-logo">
            <span className="logo-text">AMR</span>
            <span className="logo-subtext">Artist Portal</span>
          </Link>
        </div>

        <nav className="sidebar-nav">
          {/* Dashboard — standalone above groups */}
          <Link to="/artist/dashboard" data-label="Dashboard" className={`nav-item ${location.pathname === '/artist/dashboard' ? 'active' : ''}`} data-testid="nav-dashboard">
            <Home size={20} />
            <span className="nav-label">Dashboard</span>
          </Link>

          {/* GROUP 1: MUSIC */}
          <div className="nav-section-label" data-testid="section-music">
            <span>Music</span>
          </div>
          <Link to="/artist/profile" data-label="My Profile" className={`nav-item ${location.pathname === '/artist/profile' ? 'active' : ''}`} data-testid="nav-profile">
            <User size={20} />
            <span className="nav-label">My Profile</span>
          </Link>
          <Link to="/artist/appearances" data-label="Appearances" className={`nav-item ${location.pathname === '/artist/appearances' ? 'active' : ''}`} data-testid="nav-appearances">
            <Calendar size={20} />
            <span className="nav-label">Appearances</span>
          </Link>
          <Link to="/artist/submissions" data-label="Track Submissions" className={`nav-item ${location.pathname === '/artist/submissions' ? 'active' : ''}`} data-testid="nav-submissions">
            <Upload size={20} />
            <span className="nav-label">Track Submissions</span>
          </Link>

          {/* GROUP 2: MARKETING */}
          <div className="nav-section-label" data-testid="section-marketing">
            <span>Marketing</span>
          </div>
          <Link to="/artist/media" data-label="Media Vault" className={`nav-item ${location.pathname === '/artist/media' ? 'active' : ''}`} data-testid="nav-media">
            <FolderOpen size={20} />
            <span className="nav-label">Media Vault</span>
          </Link>
          <Link to="/artist/links" data-label="Link Page" className={`nav-item ${location.pathname === '/artist/links' ? 'active' : ''}`} data-testid="nav-links">
            <LinkIcon size={20} />
            <span className="nav-label">Link Page</span>
          </Link>
          {(() => {
            const pkSlug = artistData?.artist_name ? artistData.artist_name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') : null;
            return (
              <>
                <button
                  className={`nav-item nav-external${!pkSlug ? ' nav-coming-soon' : ''}`}
                  data-label="My Press Kit"
                  data-testid="nav-presskit"
                  disabled={!pkSlug}
                  onClick={() => pkSlug && window.open(`/presskit/${pkSlug}`, '_blank')}
                >
                  <ExternalLink size={20} />
                  <span className="nav-label">My Press Kit</span>
                </button>
                <button
                  className={`nav-item nav-external${!pkSlug ? ' nav-coming-soon' : ''}`}
                  data-label="Download Press Kit PDF"
                  data-testid="nav-presskit-pdf"
                  disabled={!pkSlug}
                  onClick={() => pkSlug && window.open(`${API_URL}/api/presskit/${pkSlug}/pdf`, '_blank')}
                >
                  <Download size={20} />
                  <span className="nav-label">Press Kit PDF</span>
                </button>
              </>
            );
          })()}

          {/* GROUP 3: LEGAL */}
          <div className="nav-section-label" data-testid="section-legal">
            <span>Legal</span>
          </div>
          <Link to="/artist/legal" data-label="Legal Vault" className={`nav-item ${location.pathname === '/artist/legal' ? 'active' : ''}`} data-testid="nav-legal">
            <PenTool size={20} />
            <span className="nav-label">Legal Vault</span>
            {unsignedAgreements.length > 0 && (
              <span className="nav-badge">{unsignedAgreements.length}</span>
            )}
          </Link>

        </nav>

        <div className="sidebar-footer">
          <button onClick={handleLogout} data-label="Sign Out" className="logout-btn">
            <LogOut size={18} />
            <span className="nav-label">Sign Out</span>
          </button>
        </div>
      </aside>

      {/* Bottom Tab Bar — mobile only, 3 groups */}
      <nav className="bottom-tab-bar" data-testid="bottom-tab-bar">
        <Link to="/artist/profile" className={`tab-item ${['/artist/profile', '/artist/appearances', '/artist/submissions'].includes(location.pathname) ? 'active' : ''}`} data-testid="tab-music">
          <Music size={22} />
          <span>Music</span>
        </Link>
        <Link to="/artist/media" className={`tab-item ${['/artist/media', '/artist/links'].includes(location.pathname) ? 'active' : ''}`} data-testid="tab-marketing">
          <Megaphone size={22} />
          <span>Marketing</span>
        </Link>
        <Link to="/artist/legal" className={`tab-item ${location.pathname === '/artist/legal' ? 'active' : ''}`} data-testid="tab-legal">
          <Scale size={22} />
          <span>Legal</span>
        </Link>
      </nav>

      {/* Mobile Drawer Overlay */}
      {drawerOpen && (
        <div className="drawer-backdrop" data-testid="drawer-backdrop" onClick={() => setDrawerOpen(false)} />
      )}

      {/* Mobile Slide-Up Drawer */}
      <div className={`mobile-drawer ${drawerOpen ? 'open' : ''}`} data-testid="mobile-drawer">
        <div className="drawer-header">
          <span className="drawer-title">Menu</span>
          <button className="drawer-close" data-testid="drawer-close" onClick={() => setDrawerOpen(false)}>
            <X size={24} />
          </button>
        </div>
        <nav className="drawer-nav">
          <Link to="/artist/dashboard" className={`drawer-item ${location.pathname === '/artist/dashboard' ? 'active' : ''}`}>
            <Home size={20} />
            <span>Dashboard</span>
          </Link>

          <div className="drawer-section-label">Music</div>
          <Link to="/artist/profile" className={`drawer-item ${location.pathname === '/artist/profile' ? 'active' : ''}`}>
            <User size={20} />
            <span>My Profile</span>
          </Link>
          <Link to="/artist/appearances" className={`drawer-item ${location.pathname === '/artist/appearances' ? 'active' : ''}`}>
            <Calendar size={20} />
            <span>Appearances</span>
          </Link>
          <Link to="/artist/submissions" className={`drawer-item ${location.pathname === '/artist/submissions' ? 'active' : ''}`}>
            <Upload size={20} />
            <span>Track Submissions</span>
          </Link>

          <div className="drawer-section-label">Marketing</div>
          <Link to="/artist/media" className={`drawer-item ${location.pathname === '/artist/media' ? 'active' : ''}`}>
            <FolderOpen size={20} />
            <span>Media Vault</span>
          </Link>
          <Link to="/artist/links" className={`drawer-item ${location.pathname === '/artist/links' ? 'active' : ''}`}>
            <LinkIcon size={20} />
            <span>Link Page</span>
          </Link>
          {(() => {
            const pkSlug = artistData?.artist_name ? artistData.artist_name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') : null;
            return pkSlug ? (
              <>
                <button className="drawer-item" onClick={() => { setDrawerOpen(false); window.open(`/presskit/${pkSlug}`, '_blank'); }}>
                  <ExternalLink size={20} />
                  <span>My Press Kit</span>
                </button>
                <button className="drawer-item" onClick={() => { setDrawerOpen(false); window.open(`${API_URL}/api/presskit/${pkSlug}/pdf`, '_blank'); }}>
                  <Download size={20} />
                  <span>Press Kit PDF</span>
                </button>
              </>
            ) : null;
          })()}

          <div className="drawer-section-label">Legal</div>
          <Link to="/artist/legal" className={`drawer-item ${location.pathname === '/artist/legal' ? 'active' : ''}`}>
            <PenTool size={20} />
            <span>Legal Vault</span>
          </Link>

          <button onClick={handleLogout} className="drawer-item drawer-logout">
            <LogOut size={20} />
            <span>Sign Out</span>
          </button>
        </nav>
      </div>

      {/* Main Content */}
      <main className="dashboard-main">
        {/* Mobile menu button for sub-pages (hidden on dashboard home which has its own) */}
        {location.pathname !== '/artist/dashboard' && (
          <button className="mobile-menu-float" data-testid="mobile-menu-float" onClick={() => setDrawerOpen(true)}>
            <Menu size={22} />
          </button>
        )}

        {location.pathname === '/artist/legal' ? (
          <LegalVault />
        ) : location.pathname === '/artist/links' ? (
          <LinksManager />
        ) : location.pathname === '/artist/profile' ? (
          <ProfileEditor />
        ) : location.pathname === '/artist/appearances' ? (
          <Appearances />
        ) : location.pathname === '/artist/media' ? (
          <MediaVault />
        ) : location.pathname.startsWith('/artist/submissions') ? (
          <Submissions />
        ) : (
          <>
            <header className="dashboard-header">
              <div className="welcome-section">
                <button className="mobile-menu-btn" data-testid="mobile-menu-btn" onClick={() => setDrawerOpen(true)}>
                  <Menu size={24} />
                </button>
                <h1>Welcome back, {artistData?.artist_name}!</h1>
                <p>Manage your artist profile and submissions</p>
              </div>
              <div className="header-actions">
                <Link to="/artist/profile" className="btn-primary">
                  <Edit size={18} />
                  Edit Profile
                </Link>
              </div>
            </header>

            {/* Status Cards */}
            <div className="status-cards">
              {/* Profile Completion */}
              <div className="status-card">
                <div className="card-icon profile">
                  <User size={24} />
                </div>
                <div className="card-content">
                  <h3>Profile Completion</h3>
                  <div className="progress-bar">
                    <div 
                      className="progress-fill"
                      style={{ width: `${profileCompletion}%` }}
                    />
                  </div>
                  <p>{profileCompletion}% complete</p>
            </div>
            <Link to="/artist/profile" className="card-action">
              <ChevronRight size={20} />
            </Link>
          </div>

          {/* Account Status */}
          <div className="status-card">
            <div className={`card-icon status ${artistData?.status}`}>
              {artistData?.status === 'approved' ? (
                <CheckCircle size={24} />
              ) : artistData?.status === 'pending' ? (
                <Clock size={24} />
              ) : (
                <AlertCircle size={24} />
              )}
            </div>
            <div className="card-content">
              <h3>Account Status</h3>
              <p className={`status-badge ${artistData?.status}`}>
                {artistData?.status?.charAt(0).toUpperCase() + artistData?.status?.slice(1)}
              </p>
            </div>
          </div>

          {/* Pending Agreements */}
          <div className="status-card">
            <div className={`card-icon agreements ${unsignedAgreements.length > 0 ? 'warning' : ''}`}>
              <FileText size={24} />
            </div>
            <div className="card-content">
              <h3>Agreements</h3>
              <p>
                {unsignedAgreements.length > 0 
                  ? `${unsignedAgreements.length} pending signature${unsignedAgreements.length > 1 ? 's' : ''}`
                  : 'All signed'}
              </p>
            </div>
            {unsignedAgreements.length > 0 && (
              <Link to="/artist/legal" className="card-action warning">
                <ChevronRight size={20} />
              </Link>
            )}
          </div>

          {/* Public Profile */}
          <div className="status-card">
            <div className={`card-icon public ${artistData?.public_artist_id ? 'active' : ''}`}>
              <Globe size={24} />
            </div>
            <div className="card-content">
              <h3>Public Profile</h3>
              <p>
                {artistData?.public_artist_id 
                  ? 'Published on AMR website'
                  : 'Not yet published'}
              </p>
            </div>
            {artistData?.public_artist_id && (
              <a 
                href={`/artists`} 
                target="_blank" 
                rel="noopener noreferrer"
                className="card-action"
              >
                <ExternalLink size={20} />
              </a>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <section className="dashboard-section">
          <h2>Quick Actions</h2>
          <div className="quick-actions">
            <Link to="/artist/profile" className="action-card">
              <div className="action-icon">
                <User size={28} />
              </div>
              <h3>Update Profile</h3>
              <p>Edit your artist info, bio, and media</p>
            </Link>
            <Link to="/artist/submissions/new" className="action-card">
              <div className="action-icon">
                <Upload size={28} />
              </div>
              <h3>New Submission</h3>
              <p>Upload demos or press materials</p>
            </Link>
            <Link to="/artist/legal" className="action-card">
              <div className="action-icon">
                <FileText size={28} />
              </div>
              <h3>Legal Vault</h3>
              <p>Review agreements and documents</p>
            </Link>
          </div>
        </section>

        {/* Recent Submissions */}
        {submissions.length > 0 && (
          <section className="dashboard-section">
            <div className="section-header">
              <h2>Recent Submissions</h2>
              <Link to="/artist/submissions">View All</Link>
            </div>
            <div className="submissions-list">
              {submissions.slice(0, 3).map(sub => (
                <div key={sub.id} className="submission-item">
                  <div className="submission-info">
                    <h4>{sub.title}</h4>
                    <span className="submission-type">{sub.submission_type}</span>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
          </>
        )}
      </main>
    </div>
  );
};

export default ArtistDashboard;
